/**
 * GlobalView — Sea Layer Renderer
 *
 * Renders vessel/ship entities on the Cesium globe as billboards.
 * Uses BillboardCollection + LabelCollection for performance.
 * Follows the same pattern as RailLayerRenderer.
 *
 * Ships are rendered at sea level (alt=0) with disableDepthTestDistance
 * to ensure visibility without expensive height sampling.
 */

import * as Cesium from 'cesium';
import { useEntityStore, selectShips } from '../../stores/entityStore';
import { useGlobeStore } from '../../stores/globeStore';
import { getVesselIconDataUri } from './vesselIcons';
import type { Entity as GVEntity, ShipProperties } from '@globalview/shared';

// ─── State ───────────────────────────────────────────────────────

let viewer: Cesium.Viewer | null = null;
let billboards: Cesium.BillboardCollection | null = null;
let labels: Cesium.LabelCollection | null = null;
let unsubEntity: (() => void) | null = null;
let unsubGlobe: (() => void) | null = null;

const billboardMap = new Map<string, Cesium.Billboard>();
const labelMap = new Map<string, Cesium.Label>();

// ─── Render Helpers ──────────────────────────────────────────────

function updateEntity(entity: GVEntity, visible: boolean, labelsVisible: boolean): void {
  if (!billboards || !labels || !viewer) return;

  const props = entity.properties as unknown as ShipProperties;
  const shipType = props.shipType ?? 'Unknown';
  const shipName = props.name ?? props.mmsi ?? '';

  const position = Cesium.Cartesian3.fromDegrees(
    entity.position.lon,
    entity.position.lat,
    0, // Sea level
  );

  // ─── Billboard ───────────────────────────────────────────
  let bb = billboardMap.get(entity.id);

  if (bb) {
    bb.position = position;
    bb.show = visible;
    // Update rotation when heading changes
    if (entity.heading !== undefined) {
      bb.rotation = -Cesium.Math.toRadians(entity.heading);
    }
  } else {
    bb = billboards.add({
      position,
      image: getVesselIconDataUri(shipType),
      scale: 0.8,
      show: visible,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      heightReference: Cesium.HeightReference.NONE,
      id: entity.id,
      rotation: entity.heading !== undefined ? -Cesium.Math.toRadians(entity.heading) : 0,
      // Scale down with distance
      scaleByDistance: new Cesium.NearFarScalar(5e4, 1.0, 2e6, 0.3),
    });
    billboardMap.set(entity.id, bb);
  }

  // ─── Label ───────────────────────────────────────────────
  let lbl = labelMap.get(entity.id);

  if (lbl) {
    lbl.position = position;
    lbl.text = shipName;
    lbl.show = visible && labelsVisible;
  } else {
    lbl = labels.add({
      position,
      text: shipName,
      font: '11px Inter, Helvetica, sans-serif',
      fillColor: Cesium.Color.fromCssColorString('#e0f7ff'),
      outlineColor: Cesium.Color.BLACK,
      outlineWidth: 2,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      pixelOffset: new Cesium.Cartesian2(14, -4),
      show: visible && labelsVisible,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      heightReference: Cesium.HeightReference.NONE,
      distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 500_000),
      scaleByDistance: new Cesium.NearFarScalar(1e4, 1.0, 5e5, 0.0),
    });
    labelMap.set(entity.id, lbl);
  }
}

function removeEntity(id: string): void {
  const bb = billboardMap.get(id);
  if (bb && billboards) {
    billboards.remove(bb);
    billboardMap.delete(id);
  }

  const lbl = labelMap.get(id);
  if (lbl && labels) {
    labels.remove(lbl);
    labelMap.delete(id);
  }
}

// ─── Sync (throttled) ────────────────────────────────────────────

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function requestSync(): void {
  if (syncTimer) return;
  syncTimer = setTimeout(() => {
    syncTimer = null;
    doSync();
  }, 500);
}

function doSync(): void {
  if (!viewer || viewer.isDestroyed()) return;

  const ships = selectShips(useEntityStore.getState());
  const { seaLayerVisible, seaLabelsVisible } = useGlobeStore.getState();

  const activeIds = new Set<string>();

  for (const entity of ships) {
    activeIds.add(entity.id);
    updateEntity(entity, seaLayerVisible, seaLabelsVisible);
  }

  for (const id of billboardMap.keys()) {
    if (!activeIds.has(id)) {
      removeEntity(id);
    }
  }

  viewer.scene.requestRender();
}

// ─── Public API ──────────────────────────────────────────────────

export function initSeaLayer(v: Cesium.Viewer): void {
  viewer = v;

  billboards = new Cesium.BillboardCollection({ scene: v.scene });
  labels = new Cesium.LabelCollection({ scene: v.scene });

  v.scene.primitives.add(billboards);
  v.scene.primitives.add(labels);

  unsubEntity = useEntityStore.subscribe(requestSync);

  let prevSeaVis = useGlobeStore.getState().seaLayerVisible;
  let prevLabelVis = useGlobeStore.getState().seaLabelsVisible;
  unsubGlobe = useGlobeStore.subscribe((state) => {
    if (state.seaLayerVisible !== prevSeaVis || state.seaLabelsVisible !== prevLabelVis) {
      prevSeaVis = state.seaLayerVisible;
      prevLabelVis = state.seaLabelsVisible;
      doSync();
    }
  });

  console.log('[SeaLayer] Initialized');
}

export function destroySeaLayer(): void {
  unsubEntity?.();
  unsubGlobe?.();
  unsubEntity = null;
  unsubGlobe = null;

  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }

  if (viewer && !viewer.isDestroyed()) {
    if (billboards) viewer.scene.primitives.remove(billboards);
    if (labels) viewer.scene.primitives.remove(labels);
  }

  billboards = null;
  labels = null;
  billboardMap.clear();
  labelMap.clear();
  viewer = null;

  console.log('[SeaLayer] Destroyed');
}

export function highlightSeaEntity(id: string | null): void {
  for (const [, bb] of billboardMap) {
    bb.scale = 0.8;
  }

  if (id) {
    const bb = billboardMap.get(id);
    if (bb) {
      bb.scale = 1.3;
    }
  }

  viewer?.scene.requestRender();
}

/**
 * Get the current rendered Cartesian3 position of a ship billboard.
 * Used by SelectionMarker to track the actual rendered position.
 */
export function getSeaBillboardPosition(entityId: string): Cesium.Cartesian3 | null {
  const bb = billboardMap.get(entityId);
  if (!bb) return null;
  return bb.position as Cesium.Cartesian3;
}
